/**
 * Sourcing by purchaser — manager-only (Akhil, 2026-09-19: "how many supplier comparisons has he
 * used, and which supplier does he choose most, so we can check the purchaser is not being
 * lazy"). Numbers side by side, supplier named, no score: see `lib/analytics/sourcing.ts` for
 * what each column means and why none of them is a verdict on its own.
 */
import type { PurchaserSourcing } from '../../lib/analytics/sourcing'
import { BoardCard } from './BoardCard'
import { Track, pct } from './charts'

const WARN_SHARE = 0.8

export function SourcingBoard({
  rows,
  isLoading,
  error,
}: {
  rows: PurchaserSourcing[] | undefined
  isLoading: boolean
  error: unknown
}) {
  return (
    <BoardCard
      title="Sourcing by purchaser"
      subtitle="How widely each purchaser asks, whether the comparison is used, and who they choose. Same range and filters as the boards above."
      isLoading={isLoading}
      error={error}
      isEmpty={!!rows && rows.length === 0}
      emptyLabel="No enquiries in this range."
    >
      <div style={{ color: 'var(--muted)', fontSize: 12, margin: '8px 0 10px', lineHeight: 1.5 }}>
        <b>How to read it.</b> For each purchaser's enquiries in this range: <b>Suppliers asked / enq</b> is how many
        RFQs went out per enquiry (1.0 means one supplier every time). <b>Single-supplier</b> counts the enquiries where only
        one supplier was ever asked — the price was never tested. <b>Quotes in</b> is what came back. <b>Comparison used</b>
        is how often the comparison workbench was saved on enquiries that had two or more quotes to compare — the ones
        where skipping it means a supplier was picked without looking. <b>Chooses most</b> is the supplier holding the
        biggest share of the lines this purchaser allocated, with the next three: a high share across many lines and
        suppliers is worth a question; a high share in a category with one real supplier is not.
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', fontSize: 12.5 }}>
          <thead>
            <tr>
              <th>Purchaser</th>
              <th className="right">Enquiries</th>
              <th className="right" title="RFQs sent per enquiry that had at least one">Suppliers asked / enq</th>
              <th className="right" title="Enquiries where exactly one supplier was asked — nothing to compare">Single-supplier</th>
              <th className="right" title="Supplier quotations received">Quotes in</th>
              <th className="right" title="Enquiries with a saved comparison, of those with two or more quotes — where comparing meant something">Comparison used</th>
              <th title="Who got the allocated lines, by share of this purchaser's allocated lines">Chooses most</th>
            </tr>
          </thead>
          <tbody>
            {(rows ?? []).map((r) => {
              const top = r.top_suppliers[0]
              const singleShare = r.enquiries > 0 ? r.single_supplier_enquiries / r.enquiries : 0
              const comparedShare = r.comparable_enquiries > 0 ? r.comparisons_used / r.comparable_enquiries : null
              return (
                <tr key={r.purchaser}>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.purchaser_name || r.purchaser}</div>
                    <div style={{ color: 'var(--muted)', fontSize: 11 }}>{r.suppliers_asked.length} supplier(s) asked in all</div>
                  </td>
                  <td className="right mono">{r.enquiries}</td>
                  <td className="right mono">{r.rfqs > 0 ? r.avg_suppliers_asked.toFixed(1) : '—'}</td>
                  <td className="right mono" style={singleShare >= 0.5 && r.enquiries >= 3 ? { color: 'var(--warn)', fontWeight: 700 } : undefined}>
                    {r.single_supplier_enquiries}
                    <span style={{ color: 'var(--muted)' }}> · {pct(singleShare)}</span>
                  </td>
                  <td className="right mono">{r.quotes_received}</td>
                  <td className="right mono" style={comparedShare !== null && comparedShare < 0.5 ? { color: 'var(--warn)', fontWeight: 700 } : undefined}>
                    {comparedShare !== null ? (
                      <>
                        {r.comparisons_used}
                        <span style={{ color: 'var(--muted)' }}> of {r.comparable_enquiries} · {pct(comparedShare)}</span>
                      </>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>nothing to compare</span>
                    )}
                    {r.comparisons_total > r.comparisons_used ? (
                      <div style={{ color: 'var(--muted)', fontSize: 11 }} title="Comparisons saved on enquiries with a single quote — stock or contract lines, not a choice between suppliers">
                        +{r.comparisons_total - r.comparisons_used} on one-quote enquiries
                      </div>
                    ) : null}
                  </td>
                  <td>
                    {top ? (
                      <div>
                        <b style={top.share >= WARN_SHARE && r.allocated_lines >= 5 ? { color: 'var(--warn)' } : undefined}>{top.supplier}</b>{' '}
                        <span className="mono" style={{ color: 'var(--muted)' }}>
                          {pct(top.share)} of {r.allocated_lines} lines
                        </span>
                        <Track value={top.lines} max={r.allocated_lines} tone={top.share >= WARN_SHARE && r.allocated_lines >= 5 ? 'warn' : 'brand'} />
                        {r.top_suppliers.length > 1 ? (
                          <div style={{ color: 'var(--muted)', fontSize: 11, marginTop: 2 }}>
                            then {r.top_suppliers.slice(1, 4).map((s) => `${s.supplier} ${pct(s.share)}`).join(' · ')}
                          </div>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ color: 'var(--muted)' }}>no allocation yet</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      <div style={{ color: 'var(--muted)', fontSize: 11.5, marginTop: 8 }}>
        Amber: half or more enquiries with a single supplier asked, the comparison used on fewer than half of the comparable ones, or one supplier taking {pct(WARN_SHARE)}+ of the allocated lines. A signal to look, not a verdict — a category with one real supplier looks the same.
      </div>
    </BoardCard>
  )
}
